import { useEffect, useState } from 'react';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { usePurchaseData } from '@24i/nxg-sdk-smartott-shared/src/context/PurchaseData';
import { OfferType } from '@24i/nxg-sdk-photon';
import useAvailableSubscriptionsQuery from '../../../../../hooks/query/subscriptions/useAvailableSubscriptions';
import { WithWebHeaderProps } from '../../../../../navigation/components/withWebHeader/types';
import { SOTT_DEFAULT_WEB_SCREENS } from '../../../../../navigation/constants';

import {
    getMySubscriptionsScreenLink,
    getPaymentResultScreenLink,
} from '../../../../../utils/navigation';

import {
    getStripeProductDataFromStorage,
    removeStripeProductDataFromStorage,
} from '../../../utils';
import useEntitledSubscriptionsQuery from '../../../../../hooks/query/subscriptions/useEntitledSubscriptionsQuery';

export const useViewModel = (props: WithWebHeaderProps) => {
    const router = useRouter();
    const [sessionId, setSessionId] = useState<string | null>(null);
    const purchaseDataClient = usePurchaseData();
    const { data: subscriptionData } = useAvailableSubscriptionsQuery();
    const { refetch } = useEntitledSubscriptionsQuery();
    useEffect(() => {
        if (router.isReady && typeof router.query.session_id === 'string') {
            setSessionId(router.query.session_id);
        }
    }, [router.isReady, router.query.session_id]);

    const onPaymentSuccess = async () => {
        await refetch();
        return router.push(
            getPaymentResultScreenLink({
                offerType: OfferType.SUBSCRIPTION,
                onContinueUrl: getMySubscriptionsScreenLink().as as string,
            }).as as string
        );
    };

    const onPaymentError = async () => {
        const stripeProductData = await getStripeProductDataFromStorage();
        return router.push(
            getPaymentResultScreenLink({
                offerType: OfferType.SUBSCRIPTION,
                onCancelUrl:
                    stripeProductData?.referrerRoute ||
                    (getMySubscriptionsScreenLink().as as string),
                onTryAgainUrl: stripeProductData?.productId
                    ? (SOTT_DEFAULT_WEB_SCREENS.PurchaseSubscription.getLink({
                          offerId: stripeProductData?.productId,
                      }).as as string)
                    : (getMySubscriptionsScreenLink().as as string),
            }).as as string
        );
    };

    const handleProcessPaymentReceipt = async () => {
        const stripeProductData = await getStripeProductDataFromStorage();
        const foundSubscription = subscriptionData?.subscriptions.find(
            (v) =>
                v.id === stripeProductData?.productId ||
                v.externalAccessValue === stripeProductData?.productId
        );

        if (!stripeProductData || !stripeProductData?.productId || !foundSubscription?.id) {
            return onPaymentError();
        }

        return purchaseDataClient
            .processPayment(foundSubscription?.id, {
                platform: 'stripe',
                receiptPayload: {
                    sessionId: sessionId as string,
                },
            })
            .then(onPaymentSuccess)
            .catch(onPaymentError)
            .finally(removeStripeProductDataFromStorage);
    };

    useEffect(() => {
        if (!sessionId || !subscriptionData?.subscriptions?.length) return;

        handleProcessPaymentReceipt().catch();
    }, [sessionId, subscriptionData]);

    return props;
};
